import React, { FC, useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/core';

import { SLOGraph } from '@/controllers';
import FaultZone from '@/components/composed/slo-card/graph/fault-zone';
import { GraphState } from '@/components/composed/slo-card/graph/types';
import GraphTabs from '@/components/composed/new-slo-views/expanded/graph/graph-tabs';
import { InfoPanel } from '@/components/composed/info-panel';

import { mapToDataPointEventTotals, populateDailyDataTotals } from '../../common/utils';
import { EXPANDED_GRAPH_WIDTH } from '../../common/constants';

import ErrorBudgetGraph from './error-budget-graph';

const graphRootStyles = css`
  /* Fix the dimensions so they do not change on tab transition. */
  width: ${EXPANDED_GRAPH_WIDTH}px;
  height: 100%;
`;

const ExpandedGraph: FC<SLOGraph & { onEdit: () => void }> = ({
  error,
  warnings,
  errorBudgetDomain,
  dataPoints,
  errorBudgetDataPoints,
  onEdit,
  publicId,
  performance,
}) => {
  const [graphState, setToggleState] = useState<GraphState>(GraphState.ShowErrorBudgetGraph);

  const { data, errorBudgetData } = useMemo(
    () => ({
      data: mapToDataPointEventTotals(dataPoints),
      errorBudgetData: populateDailyDataTotals(errorBudgetDataPoints, dataPoints),
    }),
    [dataPoints, errorBudgetDataPoints]
  );

  const [isInfoPanelOpen, setInfoPanelOpen] = useState<boolean>(false);
  const [infoPanelPayload, setInfoPanelPayload] = useState<any>({});

  const selectionCallback = useCallback(
    (payloads: any) => {
      setInfoPanelOpen(!!payloads?.length);
      setInfoPanelPayload(payloads.length > 0 ? payloads[0].payload : {});
    },
    [setInfoPanelOpen]
  );

  if (error && !(warnings.length > 0)) {
    return (
      <FaultZone
        performance={performance}
        error={error}
        warnings={warnings}
        onSloEdit={onEdit}
        publicId={publicId!}
      />
    );
  }

  return (
    <div css={[graphRootStyles]}>
      {warnings.length > 0 && (
        <GraphTabs setToggleState={setToggleState} graphState={graphState} align="left" />
      )}

      {graphState === GraphState.ShowWarnings ? (
        error &&
        warnings.length > 0 && (
          <FaultZone
            performance={performance}
            error={error}
            warnings={warnings}
            onSloEdit={onEdit}
            publicId={publicId!}
          />
        )
      ) : (
        <div>
          <ErrorBudgetGraph
            data={data}
            errorBudgetData={errorBudgetData}
            errorBudgetDataDomain={errorBudgetDomain}
            selectionCallback={selectionCallback}
            showClickPrompt={!isInfoPanelOpen}
          />
          <InfoPanel
            open={isInfoPanelOpen}
            payload={infoPanelPayload}
            onClose={() => {
              setInfoPanelOpen(!isInfoPanelOpen);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(ExpandedGraph);
